import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from '../../../components/forms/DatePicker';
import EmptyScreenView from '../../../components/misc/EmptyScreenView';
import { Event, EventParticipationAnswer } from '../../../../types/event';

type MemberShiftCount = {
  id: string;
  name: string;
  shiftCount: number;
  isExternal?: boolean;
};

type NoShowMember = {
  id: string;
  name: string;
  noShowCount: number;
  isExternal?: boolean;
};

type ScheduleInsightsTabProps = {
  shifts: Event[];
  t: (key: string, options?: Record<string, unknown>) => string;
};

function getShiftDate(shift: Event): Date {
  return new Date(shift.startTime);
}

function isInDateRange(date: Date, startDate?: Date, endDate?: Date): boolean {
  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
}

function formatPercentage(value: number): string {
  return `${Math.round(value)}%`;
}

function getResponseCounts(participants: Event['participants']): {
  yes: number;
  maybe: number;
  no: number;
  pending: number;
  responded: number;
  total: number;
} {
  const counts = {
    yes: 0,
    maybe: 0,
    no: 0,
    pending: 0,
    responded: 0,
    total: participants.length,
  };

  participants.forEach((participant) => {
    switch (participant.answer) {
      case EventParticipationAnswer.YES:
        counts.yes += 1;
        counts.responded += 1;
        break;
      case EventParticipationAnswer.MAYBE:
        counts.maybe += 1;
        counts.responded += 1;
        break;
      case EventParticipationAnswer.NO:
        counts.no += 1;
        counts.responded += 1;
        break;
      default:
        counts.pending += 1;
        break;
    }
  });

  return counts;
}

export default function ScheduleInsightsTab({
  shifts,
  t,
}: ScheduleInsightsTabProps): JSX.Element {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    if (shifts.length === 0) {
      setStartDate(undefined);
      setEndDate(undefined);
      return;
    }

    const sortedDates = shifts
      .map(getShiftDate)
      .sort((a, b) => a.getTime() - b.getTime());

    setStartDate(sortedDates[0]);
    setEndDate(sortedDates[sortedDates.length - 1]);
  }, [shifts]);

  const filteredShifts = useMemo(
    () =>
      shifts.filter((shift) => {
        const shiftDate = getShiftDate(shift);
        return isInDateRange(shiftDate, startDate, endDate);
      }),
    [endDate, shifts, startDate],
  );

  const summary = useMemo(() => {
    let totalInvites = 0;
    let totalYes = 0;
    let totalResponded = 0;
    let totalAssigned = 0;
    let totalCapacity = 0;

    filteredShifts.forEach((shift) => {
      const counts = getResponseCounts(shift.participants);
      totalInvites += counts.total;
      totalYes += counts.yes;
      totalResponded += counts.responded;
      totalAssigned += shift.participants.length + shift.externalInvites.length;
      totalCapacity += shift.maximumAttendees ?? 0;
    });

    const attendanceRate = totalInvites === 0 ? 0 : (totalYes / totalInvites) * 100;
    const responseRate = totalInvites === 0 ? 0 : (totalResponded / totalInvites) * 100;
    const utilizationRate = totalCapacity === 0 ? 0 : (totalAssigned / totalCapacity) * 100;

    return {
      attendanceRate,
      responseRate,
      utilizationRate,
      totalShifts: filteredShifts.length,
    };
  }, [filteredShifts]);

  const membersByShiftCount = useMemo<MemberShiftCount[]>(() => {
    const memberStats = new Map<string, MemberShiftCount>();

    filteredShifts.forEach((shift) => {
      shift.participants.forEach((participant) => {
        const key = `member-${participant.id}`;
        const existing = memberStats.get(key) ?? {
          id: key,
          name: `${participant.firstName} ${participant.lastName}`.trim(),
          shiftCount: 0,
          isExternal: false,
        };

        existing.shiftCount += 1;

        memberStats.set(key, existing);
      });

      shift.externalInvites.forEach((invite) => {
        const key = `external-${invite.externalMemberId}`;
        const existing = memberStats.get(key) ?? {
          id: key,
          name: `${invite.firstName} ${invite.lastName}`.trim(),
          shiftCount: 0,
          isExternal: true,
        };

        existing.shiftCount += 1;
        memberStats.set(key, existing);
      });
    });

    return Array.from(memberStats.values())
      .sort((a, b) => b.shiftCount - a.shiftCount || a.name.localeCompare(b.name));
  }, [filteredShifts]);

  const noShowMembers = useMemo<NoShowMember[]>(() => {
    const memberStats = new Map<string, NoShowMember>();

    filteredShifts.forEach((shift) => {
      shift.participants.forEach((participant) => {
        if (participant.answer !== EventParticipationAnswer.NO) {
          return;
        }

        const key = `member-${participant.id}`;
        const existing = memberStats.get(key) ?? {
          id: key,
          name: `${participant.firstName} ${participant.lastName}`.trim(),
          noShowCount: 0,
          isExternal: false,
        };

        existing.noShowCount += 1;
        memberStats.set(key, existing);
      });

      // External invites currently do not expose attendance answers on this payload.
      // We include them in the no-show list with a 0 count so they are visible in insights.
      shift.externalInvites.forEach((invite) => {
        const key = `external-${invite.externalMemberId}`;
        const existing = memberStats.get(key) ?? {
          id: key,
          name: `${invite.firstName} ${invite.lastName}`.trim(),
          noShowCount: 0,
          isExternal: true,
        };

        memberStats.set(key, existing);
      });
    });

    return Array.from(memberStats.values())
      .sort((a, b) => b.noShowCount - a.noShowCount || a.name.localeCompare(b.name));
  }, [filteredShifts]);

  const hasData = filteredShifts.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="items-start gap-4">
        <div className="flex w-full items-center justify-between gap-3 max-md:flex-col max-md:items-start">
          <div>
            <h3 className="text-xl font-semibold text-primary-900">
              {t('page.event.schedules.insights.title')}
            </h3>
            <p className="text-sm">
              {t('page.event.schedules.insights.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex w-full gap-4 max-md:flex-col mt-2 [&_.MuiInputBase-root]:pr-0 [&_input]:bg-primary-900 [&_input]:text-secondary-50">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold">{t('general.from')}</span>
            <DatePicker date={startDate} setDate={setStartDate} includeTime={false} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold">{t('general.to')}</span>
            <DatePicker date={endDate} setDate={setEndDate} includeTime={false} />
          </div>
        </div>
      </div>

      {!hasData ? (
        <EmptyScreenView subtitle={t('page.event.schedules.insights.noData')} />
      ) : (
        <>
          <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col rounded-xl p-4 items-start gap-2 bg-secondary-200 bg-opacity-50">
              <span className="text-xs">
                {t('page.event.schedules.insights.kpis.attendanceRate')}
              </span>
              <strong className="text-3xl text-primary-900">{formatPercentage(summary.attendanceRate)}</strong>
            </div>

            <div className="flex flex-col rounded-xl p-4 items-start gap-2 bg-secondary-200 bg-opacity-50">
              <span className="text-xs">
                {t('page.event.schedules.insights.kpis.responseRate')}
              </span>
              <strong className="text-3xl text-primary-900">{formatPercentage(summary.responseRate)}</strong>
            </div>

            <div className="flex flex-col rounded-xl p-4 items-start gap-2 bg-secondary-200 bg-opacity-50">
              <span className="text-xs">
                {t('page.event.schedules.insights.kpis.utilizationRate')}
              </span>
              <strong className="text-3xl text-primary-900">{formatPercentage(summary.utilizationRate)}</strong>
            </div>

            <div className="flex flex-col rounded-xl p-4 items-start gap-2 bg-secondary-200 bg-opacity-50">
              <span className="text-xs">
                {t('page.event.schedules.insights.kpis.shiftsInRange')}
              </span>
              <strong className="text-3xl text-primary-900">{summary.totalShifts}</strong>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="flex flex-col rounded-xl p-4 items-start gap-3 bg-secondary-200 bg-opacity-50">
              <h4 className="text-lg font-semibold text-primary-900">
                {t('page.event.schedules.insights.memberShiftCounts')}
              </h4>
              <div className="w-full">
                {membersByShiftCount.length === 0 ? (
                  <p className="text-sm">{t('page.event.schedules.insights.noMemberData')}</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {membersByShiftCount.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between rounded-lg bg-secondary-200 px-3 py-2">
                        <span className="text-sm text-primary-900">
                          {member.name}
                        </span>
                        <span className="text-sm font-semibold text-primary-900">
                          {member.shiftCount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col rounded-xl p-4 items-start gap-3 bg-secondary-200 bg-opacity-50">
              <h4 className="text-lg font-semibold text-primary-900">
                {t('page.event.schedules.insights.noShowMembers')}
              </h4>
              <div className="w-full">
                {noShowMembers.length === 0 ? (
                  <p className="text-sm">{t('page.event.schedules.insights.noNoShowData')}</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {noShowMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between rounded-lg bg-secondary-200 px-3 py-2">
                        <span className="text-sm text-primary-900">
                          {member.name}
                        </span>
                        <span className="text-sm font-semibold text-primary-900">
                          {member.noShowCount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
